import { FC } from 'react';

import classNames from 'classnames';

import FemaleAvatarPatientTicketMessage from 'img/avatars/female-avatar-patient-ticket-message.svg?react';
import MaleAvatarPatientTicketMessage from 'img/avatars/male-avatar-patient-ticket-message.svg?react';
import Chevron from 'img/icons/arrow_selected.svg?react';
import AttentionToday from 'img/icons/attention-today.svg?react';
import CalendarEmpty from 'img/icons/calendar-empty.svg?react';
import Calendar from 'img/icons/calendar.svg?react';
import Cancel from 'img/icons/cancel.svg?react';
import CheckIndeterminate from 'img/icons/check-indeterminate.svg?react';
import Checkbox from 'img/icons/checkbox.svg?react';
import CheckedCheckbox from 'img/icons/checked-checkbox.svg?react';
import CheckedRadio from 'img/icons/checked-radio.svg?react';
import CheckMark from 'img/icons/checkmark.svg?react';
import CircleEdit from 'img/icons/circle-edit.svg?react';
import Cm from 'img/icons/cm.svg?react';
import CollapseArrow from 'img/icons/collapse-arrow.svg?react';
import Delete from 'img/icons/delete.svg?react';
import DoubleArrow from 'img/icons/double-arrow.svg?react';
import DownloadArrow from 'img/icons/download-arrow.svg?react';
import DragIndicator from 'img/icons/drag-indicator.svg?react';
import EllipsisOptions from 'img/icons/ellipsis-options.svg?react';
import EpisodeEnded from 'img/icons/episode-ended.svg?react';
import EpisodeError from 'img/icons/episode-error.svg?react';
import EpisodeExpired from 'img/icons/episode-expired.svg?react';
import Episode from 'img/icons/episode.svg?react';
import ErrorXRed from 'img/icons/error-x-red.svg?react';
import Exclamation from 'img/icons/exclamation.svg?react';
import ExternalLink from 'img/icons/external-link.svg?react';
import FirstPageArrowPagination from 'img/icons/first-page-arrow-pagination.svg?react';
import FreeText from 'img/icons/free-text.svg?react';
import Help from 'img/icons/help.svg?react';
import HighOperatorUrgency from 'img/icons/high-operator-urgency.svg?react';
import ImmediateAttention from 'img/icons/immediate-attention.svg?react';
import Info from 'img/icons/information.svg?react';
import LandPhone from 'img/icons/land-phone.svg?react';
import LastPageArrowPagination from 'img/icons/last-page-arrow-pagination.svg?react';
import LinkToArrow from 'img/icons/link-to-arrow.svg?react';
import Maximize from 'img/icons/maximize.svg?react';
import MessageGradient from 'img/icons/message-gradient.svg?react';
import Minimize from 'img/icons/minimize.svg?react';
import MobilePhone from 'img/icons/mobile-phone.svg?react';
import Moon from 'img/icons/moon.svg?react';
import NextPageArrowPagination from 'img/icons/next-page-arrow-pagination.svg?react';
import Notification from 'img/icons/notification.svg?react';
import NurseReview from 'img/icons/nurse-review.svg?react';
import OpenEllipsis from 'img/icons/open-ellipsis.svg?react';
import Pause from 'img/icons/pause.svg?react';
import PencilOutline from 'img/icons/pencil-outline.svg?react';
import Pencil from 'img/icons/pencil.svg?react';
import PhoneCallEnded from 'img/icons/phone-call-ended.svg?react';
import PhoneCall from 'img/icons/phone-call.svg?react';
import PhoneGradient from 'img/icons/phone-gradient.svg?react';
import Pill from 'img/icons/pill_ic.svg?react';
import Plus from 'img/icons/plus-icon.svg?react';
import CirclePlus from 'img/icons/plus.svg?react';
import PreviousPageArrowPagination from 'img/icons/previous-page-arrow-pagination.svg?react';
import RadioButton from 'img/icons/radio-button.svg?react';
import RefreshDisabled from 'img/icons/refresh-icon-disabled.svg?react';
import Refresh from 'img/icons/refresh-icon.svg?react';
import Remove from 'img/icons/remove.svg?react';
import Resume from 'img/icons/resume-timer.svg?react';
import SavedCall from 'img/icons/saved-call.svg?react';
import Search from 'img/icons/search.svg?react';
import Send from 'img/icons/send.svg?react';
import Settings from 'img/icons/settings.svg?react';
import Share from 'img/icons/share.svg?react';
import StandardOperatorUrgency from 'img/icons/standard-operator-urgency.svg?react';
import SuccessCheckmark from 'img/icons/success-green-checkmark.svg?react';
import Unchecked from 'img/icons/unchecked.svg?react';
import XBase from 'img/icons/x-base.svg?react';
import XDisabled from 'img/icons/x-disabled.svg?react';
import XPurple from 'img/icons/x-purple.svg?react';
import XThin from 'img/icons/x-thin.svg?react';
import XThick from 'img/icons/x.svg?react';

import './Icon.scss';

// assumption: default (no rotation) equal to icon down rotation
interface RotationProps {
  up?: boolean;
  left?: boolean;
  right?: boolean;
}

// support full rotation (left, right, up, down). default icon state is down.
const withRotation = (IconComponent: React.ComponentType<any>) => {
  const IconWithRotation: FC<RotationProps & { className?: string }> = ({
    up,
    left,
    right,
    className,
    ...props
  }) => {
    const iconClasses = classNames(className, 'icon-with-rotation', { up, left, right });
    return <IconComponent className={iconClasses} {...props} />;
  };

  return IconWithRotation;
};

const Icon = {
  // TODO: move all icons from css to this component
  Cancel,
  XThin,
  XPurple,
  XDisabled,
  XThick,
  Refresh,
  RefreshDisabled,
  CalendarEmpty,
  Calendar,
  Cm,
  LandPhone,
  MobilePhone,
  PhoneGradient,
  MessageGradient,
  Chevron: withRotation(Chevron),
  CollapseArrow: withRotation(CollapseArrow),
  DoubleArrow: withRotation(DoubleArrow),
  Info,
  CheckMark,
  CheckIndeterminate,
  CircleEdit,
  Exclamation,
  Episode,
  EpisodeEnded,
  EpisodeError,
  EpisodeExpired,
  Pill,
  Plus,
  Pencil,
  PencilOutline,
  SavedCall,
  Search,
  Pause,
  Resume,
  CirclePlus,
  Delete,
  PhoneCallEnded,
  PhoneCall,
  LinkToArrow,
  ExternalLink,
  Notification,
  EllipsisOptions,
  XBase,
  Unchecked,
  NextPageArrowPagination,
  PreviousPageArrowPagination,
  FirstPageArrowPagination,
  LastPageArrowPagination,
  OpenEllipsis,
  Checkbox,
  RadioButton,
  Remove,
  DragIndicator,
  CheckedRadio,
  CheckedCheckbox,
  FreeText,
  DownloadArrow,
  Help,
  Settings,
  ImmediateAttention,
  AttentionToday,
  NurseReview,
  HighOperatorUrgency,
  StandardOperatorUrgency,
  Send,
  Share,
  FemaleAvatarPatientTicketMessage,
  MaleAvatarPatientTicketMessage,
  Minimize,
  Maximize,
  SuccessCheckmark,
  Moon,
  ErrorXRed
};

export type IconKey = keyof typeof Icon;

export default Icon;
